// BULK Trader Revamp
import { gql, useQuery } from '@apollo/client';
import { getLocaleString } from '@assets/i18n/config';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { CircularProgress } from '@ovComponents/2-component/circularProgress/circularProgress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import {
  InputAdornment,
  TextField,
} from '@ovComponents/2-component/textField/textField';
import { round } from 'lodash-es';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const FETCH_BULK_TRADE_RUN = gql`
  query fetchBulkTradeRun($bulkTradeRunId: ObjectID!) {
    fetchBulkTradeRun(bulkTradeRunId: $bulkTradeRunId) {
      bulkTradeRun {
        id
        bulkMutualFundTrades {
          id
          ticker
          exchange
          type
          currency
          moneyAllocatedCents
        }
      }
    }
  }
`;

export type AccountBulkMutualFundTrade = {
  id: string;
  ticker: string;
  exchange: string;
  type: string;
  currency: string;
  moneyAllocatedCents: number;
};

const UpdateMutualFundTradesTable = ({
  type,
  bulkTradeRunId,
}: {
  type: 'BUY' | 'SELL';
  bulkTradeRunId: string;
}) => {
  const { t } = useTranslation('bulkTrader');
  const symbolAfter = !getLocaleString().startsWith('en');

  const [bulkTrades, setBulkTrades] = useState<AccountBulkMutualFundTrade[]>(
    [],
  );

  const { data, loading } = useQuery(FETCH_BULK_TRADE_RUN, {
    variables: {
      bulkTradeRunId,
    },
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (data) {
      setBulkTrades(
        data?.fetchBulkTradeRun?.bulkTradeRun?.bulkMutualFundTrades?.filter(
          (x: any) => x.type === type,
        ),
      );
    }
  }, [data, type]);

  return (
    <Box
      data-testid={`bulk-trader-mutual-fund-trades-table-${type.toLowerCase()}`}
    >
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          data-testid={`bulk-trader-mutual-fund-trades-loading-${type.toLowerCase()}`}
        >
          <CircularProgress sx={{ m: 18 }} />
        </Box>
      ) : !bulkTrades.length ? (
        <></>
      ) : (
        <>
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
          >
            <Typography
              sx={{ p: 2 }}
              variant='headingSmall'
              testId={`bulk-trader-mutual-fund-trades-title-${type.toLowerCase()}`}
            >
              {t(`bulkMutualFund${type}Trades`)}
            </Typography>
          </Box>
          <Table
            data-testid={`bulk-trader-mutual-fund-trades-table-${type.toLowerCase()}`}
          >
            <TableHead>
              <TableRow>
                <TableHeadCell
                  testId={`bulk-trader-mutual-fund-trades-header-security-${type.toLowerCase()}`}
                >
                  {t('security')}
                </TableHeadCell>

                <TableHeadCell
                  testId={`bulk-trader-mutual-fund-trades-header-spacer-${type.toLowerCase()}`}
                />

                <TableHeadCell
                  testId={`bulk-trader-mutual-fund-trades-header-amount-${type.toLowerCase()}`}
                >
                  {t('amountValue')}
                </TableHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bulkTrades.map(
                (line: AccountBulkMutualFundTrade, index: number) => (
                  <TableRow
                    key={line.id}
                    testId={`bulk-trader-mutual-fund-trades-row-${type.toLowerCase()}-${index}`}
                  >
                    <TableCell
                      testId={`bulk-trader-mutual-fund-trades-security-${type.toLowerCase()}-${index}`}
                    >
                      <Typography weight='bold'>{line.ticker}</Typography>
                    </TableCell>

                    <TableCell
                      testId={`bulk-trader-mutual-fund-trades-spacer-${type.toLowerCase()}-${index}`}
                    />

                    <TableCell
                      sx={{ display: 'flex', alignItems: 'center' }}
                      testId={`bulk-trader-mutual-fund-trades-amount-cell-${type.toLowerCase()}-${index}`}
                    >
                      <TextField
                        value={round(line.moneyAllocatedCents / 100, 6)}
                        sx={{ width: '12em' }}
                        size='small'
                        disabled
                        autocomplete='off'
                        testId={`bulk-trader-mutual-fund-trades-amount-input-${type.toLowerCase()}-${index}`}
                        InputProps={{
                          [symbolAfter ? 'endAdornment' : 'startAdornment']: (
                            <InputAdornment
                              position={symbolAfter ? 'end' : 'start'}
                            >
                              {line.currency === 'USD' ? 'US$' : '$'}
                            </InputAdornment>
                          ),
                          inputProps: {
                            min: 0,
                            step: 'any',
                            inputMode: 'decimal',
                          },
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>
        </>
      )}
    </Box>
  );
};

export default UpdateMutualFundTradesTable;
